export const NOVU_CLOUD_API_URL = 'https://api.novu.co';
export const NOVU_STAGING_API_URL = 'https://api.novu-staging.co';
const NOVU_CLOUD_DASHBOARD_URLS = new Set([
    'https://dashboard.novu.co',
    'https://eu.dashboard.novu.co',
    'https://dashboard.novu-staging.co',
    'https://dashboard.novu.localhost',
]);
function normalizeUrl(url) {
    return (url ?? '').replace(/\/$/, '');
}
function normalizeApiUrl(apiUrl) {
    return normalizeUrl(apiUrl);
}
export function normalizeConnectTargetOptions(apiUrlOrOptions) {
    if (typeof apiUrlOrOptions === 'string' || apiUrlOrOptions == null) {
        return { apiUrl: apiUrlOrOptions };
    }
    return apiUrlOrOptions;
}
export function isNovuStagingApiUrl(apiUrl) {
    return normalizeApiUrl(apiUrl) === NOVU_STAGING_API_URL;
}
export function isNovuLocalApiUrl(apiUrl) {
    const normalized = normalizeApiUrl(apiUrl);
    if (!normalized) {
        return false;
    }
    try {
        const hostname = new URL(normalized).hostname;
        // `.localhost` is reserved for loopback (RFC 6761) — covers the
        // `api.novu.localhost` local-dev host alongside plain localhost.
        return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
    }
    catch {
        return false;
    }
}
export function getNovuConnectPackageTag(apiUrl) {
    return isNovuStagingApiUrl(apiUrl) || isNovuLocalApiUrl(apiUrl) ? 'rc' : 'latest';
}
export function isNovuPreReleaseConnectMode(apiUrl, region) {
    return isNovuStagingApiUrl(apiUrl) || isNovuLocalApiUrl(apiUrl) || region === 'staging' || region === 'local';
}
export function getNovuScaffoldSdkTag(apiUrl, region) {
    return isNovuPreReleaseConnectMode(apiUrl, region) ? 'next' : 'latest';
}
export function getNovuConnectRegionFlag(apiUrl) {
    if (!isNovuStagingApiUrl(apiUrl)) {
        return undefined;
    }
    return '--region staging';
}
export function getNovuConnectInvocation(apiUrl) {
    return `npx novu@${getNovuConnectPackageTag(apiUrl)} connect`;
}
function shouldEmitConnectDashboardFlags(connectDashboardUrl) {
    const normalized = normalizeUrl(connectDashboardUrl);
    if (!normalized) {
        return false;
    }
    return !NOVU_CLOUD_DASHBOARD_URLS.has(normalized);
}
export function formatNovuConnectCommandForDisplay(parts) {
    return parts.join(' \\\n  ');
}
/**
 * Staging uses `--region staging` so OAuth hits dashboard.novu-staging.co.
 * Other non-US Cloud APIs keep `--api-url`. Local dev also needs dashboard URLs
 * so browser OAuth opens the same dashboard the user copied the command from.
 */
export function getNovuConnectTargetFlags(apiUrlOrOptions) {
    const options = normalizeConnectTargetOptions(apiUrlOrOptions);
    const regionFlag = getNovuConnectRegionFlag(options.apiUrl);
    if (regionFlag) {
        return [regionFlag];
    }
    const flags = [];
    const normalizedApiUrl = normalizeApiUrl(options.apiUrl);
    if (normalizedApiUrl && normalizedApiUrl !== NOVU_CLOUD_API_URL) {
        flags.push(`--api-url ${normalizedApiUrl}`);
    }
    if (shouldEmitConnectDashboardFlags(options.connectDashboardUrl)) {
        const connectDashboardUrl = normalizeUrl(options.connectDashboardUrl);
        const dashboardUrl = normalizeUrl(options.dashboardUrl) || connectDashboardUrl;
        flags.push(`--connect-dashboard-url ${connectDashboardUrl}`);
        flags.push(`--dashboard-url ${dashboardUrl}`);
    }
    return flags;
}
export function buildNovuConnectStagingHint(apiUrl) {
    if (!isNovuStagingApiUrl(apiUrl)) {
        return undefined;
    }
    return 'Use `npx novu@rc` and pass `--region staging`.';
}
//# sourceMappingURL=novu-connect-cli.js.map